import { Link } from "react-router-dom";
import { Quote } from "lucide-react";
import { motion } from "framer-motion";
import { CTAButton } from "@/components/shared/CTAButton";
import { Watermark } from "@/components/shared/Watermark";
import { FIRM } from "@/constants/firmInfo";
import { useLanguage } from "@/i18n/LanguageContext";

// Editorial "about" split used on the homepage to introduce the founding
// attorney with a large photo, watermark typography, and a pull-quote —
// breaks up the card-grid monotony with an asymmetric layout.
export const AttorneyIntro = () => {
  const { t, isSpanish } = useLanguage();
  const body = isSpanish
    ? `${FIRM.founder} fundó nuestra firma en ${FIRM.foundedYear} después de vivir el proceso migratorio junto a su propia familia. Esa perspectiva moldea la manera en que tratamos a cada cliente: con paciencia, claridad y un cuidado genuino por el resultado.`
    : `${FIRM.founder} founded our firm in ${FIRM.foundedYear} after experiencing the immigration system firsthand with her own family. That perspective shapes how we treat every client — with patience, clarity, and genuine care for the outcome.`;

  return (
    <section className="relative py-16 sm:py-20 lg:py-28 bg-white overflow-hidden">
      <Watermark word="Since 2001" position="bottom-right" className="text-[color:var(--navy-950)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div
          className="lg:col-span-5 relative"
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_70px_rgba(11,18,32,0.15)] ring-1 ring-slate-100">
            <img
              src="/images/attorney-jouliana-rinnou.jpg"
              alt={`${FIRM.founder}, ${FIRM.founderTitle} at Rinnou & Associates, P.C.`}
              className="w-full h-[380px] sm:h-[440px] object-cover object-top"
            />
            <div className="bg-[color:var(--navy-950)] px-6 py-4" data-testid="attorney-intro-caption">
              <p className="font-heading text-lg text-white leading-tight">{FIRM.founder}</p>
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--gold-400)] mt-0.5">{t("about.founderTitle")}</p>
            </div>
          </div>
          <div className="absolute -top-6 -left-6 h-24 w-24 rounded-full bg-[color:var(--gold-500)] grid place-items-center shadow-[0_16px_40px_rgba(184,154,61,0.35)] rotate-3">
            <Quote className="h-8 w-8 text-[color:var(--navy-950)]" />
          </div>
        </motion.div>

        <motion.div
          className="lg:col-span-7"
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="text-xs font-medium tracking-[0.22em] uppercase text-[color:var(--gold-500)]">{t("attorneyIntro.eyebrow")}</span>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-medium tracking-tight text-[color:var(--navy-950)]">
            {t("attorneyIntro.heading1")} <span className="italic text-[color:var(--gold-500)]">{t("attorneyIntro.headingEmphasis")}</span> {t("attorneyIntro.headingEnd")}
          </h2>
          <blockquote className="mt-6 border-l-2 border-[color:var(--gold-500)] pl-5 font-heading text-xl sm:text-2xl text-[color:var(--navy-950)] italic leading-snug">
            &ldquo;{t("attorneyIntro.quote")}&rdquo;
          </blockquote>
          <p className="mt-6 text-slate-600 leading-relaxed max-w-xl">{body}</p>
          <div className="mt-8">
            <Link to="/about" data-testid="attorney-intro-learn-more-link">
              <CTAButton variant="primary">{t("attorneyIntro.cta")}</CTAButton>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
